import { captureException } from "@sentry/react"
import {
    add,
    format,
    startOfDay,
    endOfWeek,
    isSameYear,
    formatDistanceToNowStrict,
    differenceInDays,
    startOfMonth,
    addMonths,
    endOfMonth,
    startOfWeek,
} from "date-fns"
import type { Day } from "date-fns"
import { format as formatWithTz, formatInTimeZone } from "date-fns-tz"
import type { OptionsWithTZ } from "date-fns-tz"

import COPY from "~config/copy-constants"

export const DEFAULT_TIMEZONE = "America/Chicago"
export const DEFAULT_LONG_DATE_FORMAT = "MMMM d, yyyy"
export const DATE_FORMAT_SLASH = "MM/dd/yyyy"
const DEFAULT_SHORT_DATE_FORMAT = "MMM d, yyyy"
const DEFAULT_SHORT_DATE_FORMAT_WITHOUT_YEAR = "MMM d"
const DEFAULT_TIME_FORMAT = "h:mm a"
export const DEFAULT_TIME_FORMAT_WITH_TIMEZONE = "h:mm a (zzz)"
const MONTH_AND_YEAR_FORMAT = "MMMM yyyy"
const YEAR_FORMAT = "yyyy"
export const DEFAULT_CHECK_IN_DATE_FORMAT = DEFAULT_SHORT_DATE_FORMAT
const DEFAULT_CHECK_IN_TIME_FORMAT = `'at' ${DEFAULT_TIME_FORMAT_WITH_TIMEZONE}`
export const DEFAULT_CHECK_IN_FORMAT = `${DEFAULT_CHECK_IN_DATE_FORMAT} ${DEFAULT_CHECK_IN_TIME_FORMAT}`
const DATE_TIME_FORMAT = "P p"
export const HUMAN_READABLE_CUT_OFF_IN_DAYS = 7

// Monday. Centralized so we can later drive this from outfitter settings.
// date-fns uses 0 = Sunday ... 6 = Saturday.
const WEEK_STARTS_ON: Day = 1

// We request calendar data with offset on both sides
// So when the user switches months we already have data to load
const CALENDAR_DATE_OFFSET_BACK = -1
// Current month is more likely to fall in the past month
// (because the calendar will fill the first week with the previous month)
//, so use a number greater than 1 for going forward
const CALENDAR_DATE_OFFSET_FORWARD = 2

function getLocalTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (error) {
        captureException(error)
        return DEFAULT_TIMEZONE
    }
}

export function getFullFormattedDateTime(date: Date | number | string) {
    return formatInTimeZone(date, getLocalTimeZone(), DEFAULT_CHECK_IN_FORMAT)
}

export function formatDateToMonthAndYear(date?: Date | number): string {
    if (!date) return ""

    return format(date, MONTH_AND_YEAR_FORMAT)
}

export function formatDateYear(date: Date | number): string {
    return format(date, YEAR_FORMAT)
}

// Ex. Oct 1, 2020
export function formatDateToShortName({
    date,
    shouldIncludeTime = false,
    shouldHideCurrentYear = false,
}: {
    date: Date | number | undefined | null
    shouldIncludeTime?: boolean
    shouldHideCurrentYear?: boolean
}): string {
    if (!date) return ""

    const isCurrentYear = isSameYear(new Date(date), new Date())

    const dateFormat =
        shouldHideCurrentYear && isCurrentYear
            ? DEFAULT_SHORT_DATE_FORMAT_WITHOUT_YEAR
            : DEFAULT_SHORT_DATE_FORMAT

    if (shouldIncludeTime) {
        return format(date, `${dateFormat}, ${DEFAULT_TIME_FORMAT}`)
    }

    return format(date, dateFormat)
}

export function formatDateInTimeZoneToLongName(
    date: Date | number,
    tz: string
) {
    return formatInTimeZone(date, tz, "MMMM do, yyyy")
}

export function formatDatesRange(
    start: number | Date | undefined | null,
    end: number | Date | undefined | null,
    tzOptions?: OptionsWithTZ
): string {
    if (!start || !end) return ""

    const startDate = new Date(start)
    const endDate = new Date(end)

    // If the dates are in different years, include the year in the output
    if (startDate.getFullYear() !== endDate.getFullYear()) {
        return `${formatWithTz(
            startDate,
            DEFAULT_SHORT_DATE_FORMAT,
            tzOptions
        )} - ${formatWithTz(endDate, DEFAULT_SHORT_DATE_FORMAT, tzOptions)}`
    }

    // If the dates are in different months, include the month and day for both dates
    if (startDate.getMonth() !== endDate.getMonth()) {
        return `${formatWithTz(startDate, "MMM d", tzOptions)} - ${formatWithTz(
            endDate,
            DEFAULT_SHORT_DATE_FORMAT,
            tzOptions
        )}`
    }

    // If the dates are in the same month, but different days, include both days
    if (startDate.getDate() !== endDate.getDate()) {
        return `${formatWithTz(startDate, "MMM d", tzOptions)} - ${formatWithTz(
            endDate,
            "d, yyyy",
            tzOptions
        )}`
    }

    // If the dates are the same day, only include the day once
    return `${formatWithTz(startDate, DEFAULT_SHORT_DATE_FORMAT, tzOptions)}`
}

export function formatStartAndEndDatesSelection(dates: Date[] | number[]) {
    const [startDate, endDate] = dates

    if (!startDate) return { startDate: COPY.NO_DATES_SELECTED }

    const formattedStartDate = formatDateToShortName({ date: startDate })
    // If only 1 date (representing a 1 day trip) was selected we will show that as the start and end date
    const formattedEndDate = formatDateToShortName({
        date: endDate || startDate,
    })

    return {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
    }
}

export const formatTimeStringFromMinutes = (minutes: number) => {
    const startOfToday = startOfDay(new Date())
    const time = add(startOfToday, { minutes })

    return format(time, DEFAULT_TIME_FORMAT)
}

/**
 * Standard display for any entity `created_at` (or equivalent): relative time
 * when recent, full locale date/time when older than {@link HUMAN_READABLE_CUT_OFF_IN_DAYS}.
 */
export function getHumanReadableCreatedAt(
    date: Date | number | string | null | undefined
): string {
    const valueDate = toValidDateForDisplay(date)
    if (valueDate === null) {
        return ""
    }
    const numberOfDaysOld = differenceInDays(new Date(), valueDate)
    const isOlderThanCutoff = numberOfDaysOld > HUMAN_READABLE_CUT_OFF_IN_DAYS

    return isOlderThanCutoff
        ? formatDateTime(valueDate)
        : getDistanceToNow(valueDate)
}

function toValidDateForDisplay(
    date: Date | number | string | null | undefined
): Date | null {
    if (date === null || date === undefined) {
        return null
    }
    const parsedDate =
        typeof date === "string" || typeof date === "number"
            ? new Date(date)
            : date
    if (Number.isNaN(parsedDate.getTime())) {
        return null
    }
    return parsedDate
}

export function getFromDateForCalendarQuery(currentMonth: Date) {
    const firstDayOfMonth = startOfMonth(currentMonth)

    return addMonths(firstDayOfMonth, CALENDAR_DATE_OFFSET_BACK)
}

export function getToDateForCalendarQuery(currentMonth: Date) {
    const lastDayOfMonth = endOfMonth(currentMonth)

    return addMonths(lastDayOfMonth, CALENDAR_DATE_OFFSET_FORWARD)
}

export function getDateRange(
    dates: readonly Date[]
): { start_date: number; end_date: number } | null {
    if (!dates.length) return null

    const times = dates.map((date) => date.getTime())
    return {
        start_date: Math.min(...times),
        end_date: Math.max(...times),
    }
}

export function getStartOfWeek(date: Date = new Date()): Date {
    return startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON })
}

export function getStartAndEndOfWeekEpochMs(weekStart: Date): [number, number] {
    const start = startOfDay(weekStart).getTime()
    const end = endOfWeek(weekStart, {
        weekStartsOn: WEEK_STARTS_ON,
    }).getTime()

    return [start, end]
}

function getDistanceToNow(date: Date | number) {
    const distanceString = formatDistanceToNowStrict(date)

    const [amount, distance] = distanceString.split(" ")

    return `${amount} ${distance} ${COPY.AGO}`
}

function formatDateTime(date: Date | number) {
    return format(date, DATE_TIME_FORMAT)
}

export function formatDateToDayAndYear(date: Date | number) {
    return format(date, "MMM d")
}
